import { AxiosResponse } from 'axios';
import { flatMap, partition } from 'lodash';
import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { CompanyRespWithSettingAttributes } from '~src/interfaces/company.interface';
import { CycleI, getPackNameTranslationKey, PackI, PackName } from '~src/interfaces/cycle.interface';
import { getCompanySegment, Segment, SegmentType } from '~src/interfaces/segment.interface';
import { SurveySubjectTypes } from '~src/interfaces/surveys.interfaces';
import { StoreI } from '~src/rootReducer';
import { pulseCheck } from '~src/services/ApiController';
import { translateContent } from '~src/services/translationServices';
import { GuidedTourPayload, initializeUserGuiding, selectGuidedTourPayload } from '~src/shared/guidedTour';
import LoadingSpinner from '~src/shared/loadingSpinner/LoadingSpinner';
import { TileContainer } from '~src/styles/containers.sc';

import pack_sort from './pack_sort';
import { GridHeadline, SurveysCard } from './surveyOverviewSection.sc';
import SurveysGrid from './surveysGrid';
import SurveyTile, { About } from './surveyTile';

type Props = WithTranslation & StateProps;

interface State {
  cycles?: CycleI[];
  error?: Error;
}

interface StateProps {
  company: CompanyRespWithSettingAttributes | null;
  guidedTourPayload?: GuidedTourPayload;
}

function aboutf(scheduleAbout: Segment): About {
  let m;
  switch (scheduleAbout.type) {
    case SegmentType.User:
      m = scheduleAbout.meta;
      return {
        name: m.name,
        imageUrl: scheduleAbout.image_url,
        type: SurveySubjectTypes.People,
        id: scheduleAbout.id,
      };
    case SegmentType.Group:
      m = scheduleAbout.meta;
      return {
        name: translateContent(m.option_name),
        imageUrl: scheduleAbout.image_url,
        type: SurveySubjectTypes.Groups,
        id: scheduleAbout.id,
      };
    case SegmentType.Company:
      m = scheduleAbout.meta;
      return {
        name: m.name,
        imageUrl: scheduleAbout.image_url,
        type: SurveySubjectTypes.General,
        id: scheduleAbout.id,
      };
    case SegmentType.Team:
      m = scheduleAbout.meta;
      return {
        name: m.manager_name,
        imageUrl: scheduleAbout.image_url,
        type: SurveySubjectTypes.Groups,
        id: scheduleAbout.id,
      };
    case SegmentType.Anonymous:
      return {
        name: '?',
        imageUrl: scheduleAbout.image_url,
        type: SurveySubjectTypes.General,
        id: scheduleAbout.id,
      };
  }
}
class SurveyOverviewSection extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {};
  }

  formattedStatus(cycle: CycleI, pack: PackI): string {
    const postfix = pack.finished ? '\n' + this.props.t('x.user.home.survey_overview.finished') : '';
    switch (pack.name) {
      case PackName.SelfAssessment:
      case PackName.SelfSelection:
      case PackName.CustomSelector:
      case PackName.ManagersSelection:
        return this.props.t(getPackNameTranslationKey(pack.name)) + postfix;
      case PackName.Assessment: {
        const count = cycle.survey.questionCount;
        const key =
          count !== 1
            ? 'x.user.home.survey_overview.question_count_plural'
            : 'x.user.home.survey_overview.question_count_singular';
        return this.props.t(key, { count }) + postfix;
      }
    }
  }

  componentDidMount() {
    if (this.props.guidedTourPayload) {
      void initializeUserGuiding(this.props.guidedTourPayload);
    }
    pulseCheck
      .get('/cycles?include=pack.about,schedule.about,answered')
      .then((res: AxiosResponse<CycleI[]>) => {
        const cycles = res.data;
        if (cycles) {
          this.setState({ cycles });
        } else {
          this.setState({ error: new Error('Weird answer did not contain cycles') });
        }
      })
      .catch((error) => {
        this.setState({ error });
      });
  }

  render() {
    if (this.state.error) {
      return (
        <TileContainer>
          <SurveysCard>
            {this.state.error.name} : {this.state.error.message}
          </SurveysCard>
        </TileContainer>
      );
    }
    if (!this.state.cycles) {
      return (
        <TileContainer>
          <SurveysCard>
            {!this.state.cycles && <LoadingSpinner size={30} dotsMargin={2} marginTop={30} />}
          </SurveysCard>
        </TileContainer>
      );
    }

    const cycleSegmentTiles: Array<{ finished: boolean; tile: JSX.Element }> = flatMap(
      this.state.cycles,
      (cycle: CycleI) => {
        return cycle.packs.sort(pack_sort).map((pack: PackI) => {
          pack.about =
            pack.about ||
            (this.props.company
              ? getCompanySegment(this.props.company)
              : { type: 'company', meta: { name: '\u2065' }, id: '', image_url: '' });
          /* fallback, should never happen */

          const segment: Segment = pack.about;

          const key = `${cycle.id}-${pack.name}-${segment.id}`;
          return {
            tile: (
              <SurveyTile
                key={key}
                dueDate={cycle.dueDate}
                title={translateContent(cycle.survey.name)}
                about={aboutf(segment)}
                packName={pack.name}
                cycleId={cycle.id}
                status={this.formattedStatus(cycle, pack)}
                finished={pack.finished}
              />
            ),
            finished: pack.finished,
          };
        });
      },
    );

    const [done, todo] = partition(cycleSegmentTiles, (cycleSegmentTile) => cycleSegmentTile.finished);

    return (
      <TileContainer>
        <SurveysCard>
          <GridHeadline style={{ background: 'linear-gradient(to right, #523951, #ab4f6d)', color: 'white' }}>
            {this.props.t('x.user.home.todo.title')}
          </GridHeadline>
          <SurveysGrid emptyText={this.props.t('x.user.home.todo.empty')}>
            {todo.map((t) => t.tile)}
          </SurveysGrid>
          <GridHeadline style={{ background: '#dfd' }}>{this.props.t('x.user.home.done.title')}</GridHeadline>
          <SurveysGrid emptyText={this.props.t('x.user.home.done.empty')}>
            {done.map((t) => t.tile)}
          </SurveysGrid>
        </SurveysCard>
      </TileContainer>
    );
  }
}

const SurveyOverviewSectionT = withTranslation()(SurveyOverviewSection);

const mapStateToProps = (state: StoreI): StateProps => {
  return {
    guidedTourPayload: selectGuidedTourPayload(state),
    company: state.user.session.company,
  };
};

const SurveyOverviewSectionC = connect(mapStateToProps)(SurveyOverviewSectionT);

export default SurveyOverviewSectionC;
